import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Divider,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

const palette = {
  primary: '#1F3A5F',
  secondary: '#2C8C8C',
  accent: '#D6A75B',
  bg: '#F6F4EF',
  bgDark: '#0F1E2E',
  bgCard: '#FFFFFF',
  textDark: '#1a1a2e',
  textMuted: '#5a6872',
  textLight: '#E0E0E0',
  border: '#e8e4de',
  sectionAlt: '#F0EDE6',
};

function Terms() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const sections = Array.from({ length: 11 }, (_, i) => {
    const n = i + 1;
    const section = {
      title: t(`terms_page.s${n}_title`),
      content: t(`terms_page.s${n}_content`),
    };
    if (n === 9) {
      section.link = { text: t('terms_page.s9_link_text'), path: '/privacy' };
    }
    return section;
  });

  return (
    <Box sx={{ bgcolor: palette.bg, minHeight: '100vh' }}>
      <Box
        sx={{
          bgcolor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${palette.border}`,
          py: 1.5,
          position: 'sticky',
          top: 0,
          zIndex: 1100,
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box
              component="img"
              src="/images/landing/logo-dark.webp"
              alt="URStore"
              sx={{ height: 36, cursor: 'pointer' }}
              onClick={() => navigate('/')}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/')}
                sx={{ color: palette.textMuted }}
              >
                {t('btn.back_to_home')}
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate('/apply')}
                sx={{
                  bgcolor: palette.accent,
                  color: palette.bgDark,
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 2.5,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#c4963e' },
                }}
              >
                {t('terms_page.free_trial_btn')}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ bgcolor: palette.bgDark, color: 'white', py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h2" fontWeight={800} sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, mb: 2 }}>
            {t('terms_page.title')}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.7, fontWeight: 400 }}>
            {t('terms_page.subtitle')}
          </Typography>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              bgcolor: palette.bgCard,
              border: `1px solid ${palette.border}`,
              borderRadius: 3,
              p: { xs: 3, md: 5 },
              boxShadow: '0 20px 60px rgba(0,0,0,0.06)',
            }}
          >
            {sections.map((section, index) => (
              <Box key={index}>
                {index > 0 && <Divider sx={{ my: 3, borderColor: palette.border }} />}
                <Typography variant="h6" fontWeight={700} sx={{ color: palette.primary, mb: 1.5 }}>
                  {section.title}
                </Typography>
                <Typography sx={{ color: palette.textMuted, lineHeight: 1.9, fontSize: '0.95rem' }}>
                  {section.content}
                </Typography>
                {section.link && (
                  <Button
                    size="small"
                    onClick={() => navigate(section.link.path)}
                    sx={{
                      mt: 1,
                      color: palette.secondary,
                      fontWeight: 600,
                      textDecoration: 'underline',
                      '&:hover': { bgcolor: 'transparent', color: palette.primary },
                    }}
                  >
                    {section.link.text}
                  </Button>
                )}
              </Box>
            ))}

            <Divider sx={{ my: 4, borderColor: palette.border }} />
            <Typography variant="body2" sx={{ color: palette.textMuted, textAlign: 'center' }}>
              {t('terms_page.last_updated')}
            </Typography>
          </Paper>
        </Container>
      </Box>

      <Box sx={{ bgcolor: palette.bgDark, borderTop: '1px solid rgba(255,255,255,0.06)', py: 3 }}>
        <Container maxWidth="lg">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              © 2026 URStore. All rights reserved.
            </Typography>
            <Button
              size="small"
              onClick={() => navigate('/')}
              sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}
            >
              {t('btn.back_to_home')}
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

export default Terms;
